//Keeps the four camera <video> elements playing in lockstep. The first video is the
//master: the media bar reads its clock, and drift correction snaps the others to it.
//Built for plain HTMLVideoElements (or anything with the same surface) handed in through
//a getter so the caller controls which videos take part (only the ones with a source).

export const DRIFT_TOLERANCE_S = 0.15; //cameras visibly desync beyond about this
export const SEEK_TIMEOUT_MS = 1500;   //a video that cannot seek must not hang the group

class VideoSync {

    constructor(getVideos) {
        this.getVideos = getVideos;
    }

    master() {
        const videos = this.getVideos();
        return videos.length > 0 ? videos[0] : null;
    }

    currentTime() {
        const master = this.master();
        return master ? master.currentTime : 0;
    }

    duration() {
        const master = this.master();
        return (master && master.duration) ? master.duration : 0;
    }

    isPaused() {
        const master = this.master();
        return master ? master.paused : true;
    }

    //Sets every video to the same time and resolves once they have all finished seeking
    //(or the timeout has passed -- a stalled video must not wedge the group forever).
    seekAll(seconds) {

        const seeks = this.getVideos().map((video) => new Promise((resolve) => {

            const timer = setTimeout(() => { cleanup(); }, SEEK_TIMEOUT_MS);

            const cleanup = () => {
                clearTimeout(timer);
                video.removeEventListener('seeked', cleanup);
                resolve();
            };

            video.addEventListener('seeked', cleanup);
            video.currentTime = seconds;
        }));

        return Promise.all(seeks);
    }

    //play() returns a promise that rejects e.g. when a video has no source; one bad video
    //must not stop the others
    playAll() {
        return Promise.all(this.getVideos().map((video) => {
            const p = video.play();
            return (p && p.catch) ? p.catch(() => {}) : Promise.resolve();
        }));
    }

    pauseAll() {
        this.getVideos().forEach(video => video.pause());
    }

    //The synchronised jump: land every video on the target frame first, then start them
    //together in the same tick, so none gets a head start while the others are still seeking.
    seekAndPlay(seconds) {
        return this.seekAll(seconds).then(() => this.playAll());
    }

    //Snap any video that has drifted from the master's clock; returns how many needed it.
    //Called about once a second while playing -- small corrections, rarely.
    correctDrift() {

        const videos = this.getVideos();

        if (videos.length < 2)
        {
            return 0;
        }

        const masterTime = videos[0].currentTime;
        let nudged = 0;

        for (let i = 1; i < videos.length; i++)
        {
            if (Math.abs(videos[i].currentTime - masterTime) > DRIFT_TOLERANCE_S)
            {
                videos[i].currentTime = masterTime;
                nudged++;
            }
        }

        return nudged;
    }
}

export default VideoSync;
